import { selectApi } from '@/modules/calendar/api';
import { preferencesApi } from '@/core/preferences/export';
import {
  BADGES,
  BadgeDefinition,
  evaluateNewBadges,
} from '@/modules/calendar/model/forecast-game/badges';
import {
  emptyForecastGameState,
  ForecastGameState,
  ForecastGameStats,
  ForecastOutcome,
  ForecastResult,
  PendingForecast,
} from '@/modules/calendar/model/forecast-game/forecast-game-state';
import { Precision } from '@/modules/calendar/model/calendar/data-series-entry';
import { FormattingUtil } from '@/modules/calendar/util/formatting-util';

export const FORECAST_FRENZY_WIDGET_ID = 'forecast-frenzy';
const PREFERENCES_KEY = `widget-${FORECAST_FRENZY_WIDGET_ID}`;
export const MAX_PINNED_BADGES = 3;

// Full-object PUT rather than upsertPreferences' PATCH: this service and the
// widget's own tab pages are the only writers, we always compute the full
// state client-side, and PATCH's server-side merge semantics for nested
// objects (e.g. `byIndicator`) aren't documented anywhere in this codebase.
async function getState(): Promise<ForecastGameState> {
  const raw = await preferencesApi.getPreferences<ForecastGameState>(PREFERENCES_KEY);

  // The API returns `{}` (truthy, but shape-less) rather than null/404 when
  // the key has never been saved — guard on the actual fields, not truthiness.
  if (!raw || !Array.isArray(raw.pending) || !raw.stats) return emptyForecastGameState();

  return raw;
}

async function saveState(state: ForecastGameState): Promise<ForecastGameState> {
  await preferencesApi.overwritePreferences(PREFERENCES_KEY, state);
  return state;
}

function pendingKey(dataSeriesId: number, date: string): string {
  return `${dataSeriesId}-${date}`;
}

// getState/saveState is a GET-then-PUT against the same preferences key with
// no server-side locking, so overlapping calls (e.g. two ForecastEditors on
// one calendar event) can silently drop each other's write. Routing every
// mutation through this queue serializes them within the tab.
let queue: Promise<unknown> = Promise.resolve();
function enqueue<T>(fn: () => Promise<T>): Promise<T> {
  const result = queue.then(fn, fn);
  queue = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

async function recordForecast(
  entry: {
    dataSeriesId: number;
    seriesDisplay: string;
    date: string;
    dataReleaseId?: number;
    userValue: string;
    mniValue?: string;
  },
  alreadyReleased?: boolean,
): Promise<ForecastGameState> {
  if (alreadyReleased) return removeForecast(entry.dataSeriesId, entry.date);

  return enqueue(async () => {
    const state = await getState();
    const key = pendingKey(entry.dataSeriesId, entry.date);

    const pending: PendingForecast[] = state.pending.filter(
      p => pendingKey(p.dataSeriesId, p.date) !== key,
    );
    pending.push(entry);

    return saveState({ ...state, pending });
  });
}

async function removeForecast(dataSeriesId: number, date: string): Promise<ForecastGameState> {
  return enqueue(async () => {
    const state = await getState();
    const key = pendingKey(dataSeriesId, date);

    return saveState({
      ...state,
      pending: state.pending.filter(p => pendingKey(p.dataSeriesId, p.date) !== key),
    });
  });
}

/**
 * Pins/unpins a badge to the Overview showcase. No-ops if the badge hasn't
 * been earned, or if pinning would exceed MAX_PINNED_BADGES.
 */
async function togglePinnedBadge(badgeId: string): Promise<ForecastGameState> {
  return enqueue(async () => {
    const state = await getState();

    if (!state.stats.badges.includes(badgeId)) return state;

    const pinnedBadges = state.pinnedBadges ?? [];
    const isPinned = pinnedBadges.includes(badgeId);

    if (!isPinned && pinnedBadges.length >= MAX_PINNED_BADGES) return state;

    return saveState({
      ...state,
      pinnedBadges: isPinned
        ? pinnedBadges.filter(id => id !== badgeId)
        : [...pinnedBadges, badgeId],
    });
  });
}

// Mirrors DataSeriesEntry.diff()'s rounding so a value that reads as a tie in
// the calendar/forecast-editor UI can't score as a win/loss here.
function roundToPrecision(value: string, precision?: Precision): number {
  return parseFloat((FormattingUtil.formatNumber(value, precision) as string).replaceAll(/,/g, ''));
}

/** Pure: closer-to-actual-wins comparison, same distance metric as DataSeriesEntry.diff(). */
export function computeOutcome(
  actual: string,
  userValue: string,
  mniValue?: string,
  precision?: Precision,
): ForecastOutcome | null {
  const actualNum = roundToPrecision(actual, precision);
  const userNum = roundToPrecision(userValue, precision);

  if (isNaN(actualNum) || isNaN(userNum)) return null;
  if (mniValue === undefined) return null;

  const mniNum = roundToPrecision(mniValue, precision);
  if (isNaN(mniNum)) return null;

  const userDistance = Math.abs(actualNum - userNum);
  const mniDistance = Math.abs(actualNum - mniNum);

  if (userDistance < mniDistance) return 'win';
  if (userDistance > mniDistance) return 'loss';
  return 'tie';
}

function isContrarianWin(
  outcome: ForecastOutcome,
  actual: string,
  userValue: string,
  mniValue?: string,
  precision?: Precision,
): boolean {
  if (outcome !== 'win' || mniValue === undefined) return false;

  const actualNum = roundToPrecision(actual, precision);
  const userNum = roundToPrecision(userValue, precision);
  const mniNum = roundToPrecision(mniValue, precision);

  if (isNaN(actualNum) || isNaN(userNum) || isNaN(mniNum)) return false;

  // "Contrarian" = user called the move in the opposite direction from MNI's consensus.
  return Math.sign(userNum - actualNum) !== Math.sign(mniNum - actualNum) && userNum !== mniNum;
}

function applyOutcome(stats: ForecastGameStats, outcome: ForecastOutcome): ForecastGameStats {
  const streakBefore = stats.currentStreak;
  let currentStreak = streakBefore;

  if (outcome === 'win') currentStreak = streakBefore >= 0 ? streakBefore + 1 : 1;
  else if (outcome === 'loss') currentStreak = streakBefore <= 0 ? streakBefore - 1 : -1;
  else currentStreak = 0;

  return {
    ...stats,
    wins: stats.wins + (outcome === 'win' ? 1 : 0),
    losses: stats.losses + (outcome === 'loss' ? 1 : 0),
    ties: stats.ties + (outcome === 'tie' ? 1 : 0),
    currentStreak,
    bestStreak: Math.max(stats.bestStreak, currentStreak),
  };
}

export interface ResolvedForecast {
  seriesDisplay: string;
  date: string;
  userValue: string;
  mniValue?: string;
  actual: string;
  outcome: ForecastResult;
}

function isExactMatch(userValue: string, actual: string, precision?: Precision): boolean {
  const userNum = roundToPrecision(userValue, precision);
  const actualNum = roundToPrecision(actual, precision);
  return !isNaN(userNum) && !isNaN(actualNum) && userNum === actualNum;
}

/**
 * Checks pending forecasts against real events, one API call per unique
 * dataReleaseId (not per pending item), folds resolved ones into `stats`,
 * and drops them from `pending` so the payload stays bounded. `justResolved`
 * is transient (not persisted) — only used to show the "while you were away" recap.
 */
// Unwrapped so `checkIn` (itself enqueued) can call it directly without
// nesting enqueue() calls, which would deadlock against its own pending slot.
async function resolvePendingUnlocked(): Promise<{
  state: ForecastGameState;
  justResolved: ResolvedForecast[];
}> {
  const state = await getState();

  if (state.pending.length === 0) return { state, justResolved: [] };

  const byRelease = new Map<number, PendingForecast[]>();
  for (const item of state.pending) {
    if (item.dataReleaseId === undefined) continue;
    const list = byRelease.get(item.dataReleaseId) ?? [];
    list.push(item);
    byRelease.set(item.dataReleaseId, list);
  }

  const stillPending: PendingForecast[] = state.pending.filter(p => p.dataReleaseId === undefined);
  const justResolved: ResolvedForecast[] = [];
  let stats = state.stats;

  // Fetch every release concurrently — wall-clock is bounded by the slowest
  // single call rather than the sum of all of them — then fold the resolved
  // items back together sequentially so stats/badges keep seeing a
  // consistent, ordered view of `stats`.
  const releaseEntries = [...byRelease.entries()];
  const fetchResults = await Promise.allSettled(
    releaseEntries.map(([dataReleaseId, items]) => {
      const dates = items.map(i => new Date(i.date).getTime());
      const start = Math.min(...dates) - 60_000;
      const end = Math.max(...dates) + 60_000;
      return selectApi.getCalendarForDataRelease(dataReleaseId, start, end);
    }),
  );

  for (let i = 0; i < releaseEntries.length; i++) {
    const [, items] = releaseEntries[i];
    const fetchResult = fetchResults[i];

    if (fetchResult.status === 'rejected') {
      stillPending.push(...items);
      continue;
    }
    const events = fetchResult.value;

    for (const item of items) {
      const event = events.find(e => e.date === item.date);
      const entry = event?.dataSeriesEntries.find(e => e.dataSeriesId === item.dataSeriesId);
      const actual = entry?.actual;

      if (!actual) {
        stillPending.push(item);
        continue;
      }

      // MNI's forecast may not have been published yet when the user
      // submitted theirs (item.mniValue undefined) but could be available
      // now — fall back to the live entry instead of leaving this
      // permanently unscoreable.
      const mniValue = item.mniValue ?? entry?.forecasts.find(f => f.source === 'MNI')?.value;
      const precision = entry?.precision;

      // The release is out — this item is resolved one way or another from
      // here on, even if there's nothing to score it against (no MNI
      // forecast, or unusable numbers). It must never go back to `pending`.
      const outcome = computeOutcome(actual, item.userValue, mniValue, precision);
      const exactMatch = isExactMatch(item.userValue, actual, precision);

      // Bias tracking (your value vs. the actual) doesn't need MNI at all,
      // so it's updated regardless of whether `outcome` could be computed.
      const indicator = stats.byIndicator[item.dataSeriesId] ?? {
        display: item.seriesDisplay,
        wins: 0,
        losses: 0,
        ties: 0,
        biasSum: 0,
        biasCount: 0,
      };
      const userNum = parseFloat(item.userValue);
      const actualNum = parseFloat(actual);
      const biasUsable = !isNaN(userNum) && !isNaN(actualNum);

      const outcomeDelta =
        outcome !== null
          ? {
              wins: outcome === 'win' ? 1 : 0,
              losses: outcome === 'loss' ? 1 : 0,
              ties: outcome === 'tie' ? 1 : 0,
            }
          : { wins: 0, losses: 0, ties: 0 };

      const prevStats = stats;
      if (outcome !== null) stats = applyOutcome(stats, outcome);

      stats = {
        ...stats,
        byIndicator: {
          ...stats.byIndicator,
          [item.dataSeriesId]: {
            ...indicator,
            wins: indicator.wins + outcomeDelta.wins,
            losses: indicator.losses + outcomeDelta.losses,
            ties: indicator.ties + outcomeDelta.ties,
            biasSum: indicator.biasSum + (biasUsable ? userNum - actualNum : 0),
            biasCount: indicator.biasCount + (biasUsable ? 1 : 0),
          },
        },
      };

      if (outcome !== null) {
        // A perfect call earns Bullseye and streak/specialist credit.
        const newBadges = evaluateNewBadges(prevStats, stats, {
          outcome,
          exactMatch,
          contrarianWin: isContrarianWin(outcome, actual, item.userValue, mniValue, precision),
          streakBeforeThisResolution: prevStats.currentStreak,
        });
        if (newBadges.length > 0) {
          stats = { ...stats, badges: [...stats.badges, ...newBadges] };
        }
      } else if (exactMatch && !stats.badges.includes('bullseye')) {
        // No MNI to compare against — doesn't touch wins/losses/streak/
        // specialists, but a perfect call still earns Bullseye.
        stats = { ...stats, badges: [...stats.badges, 'bullseye'] };
      }

      justResolved.push({
        seriesDisplay: item.seriesDisplay,
        date: item.date,
        userValue: item.userValue,
        mniValue,
        actual,
        outcome: outcome ?? 'no-contest',
      });
    }
  }

  const savedState = await saveState({ ...state, pending: stillPending, stats });

  return { state: savedState, justResolved };
}

async function resolvePending(): Promise<{
  state: ForecastGameState;
  justResolved: ResolvedForecast[];
}> {
  return enqueue(() => resolvePendingUnlocked());
}

export interface RecapDiff {
  newWins: number;
  newLosses: number;
  newTies: number;
  newBadges: BadgeDefinition[];
}

/** Pure: null if nothing changed (or this is the very first visit — nothing to compare against). */
function computeRecap(state: ForecastGameState): RecapDiff | null {
  if (!state.lastSeen) return null;

  const newWins = state.stats.wins - state.lastSeen.wins;
  const newLosses = state.stats.losses - state.lastSeen.losses;
  const newTies = state.stats.ties - state.lastSeen.ties;
  const newBadges = state.stats.badges
    .filter(id => !state.lastSeen!.badges.includes(id))
    .map(id => BADGES.find(b => b.id === id))
    .filter((b): b is BadgeDefinition => b !== undefined);

  if (newWins === 0 && newLosses === 0 && newTies === 0 && newBadges.length === 0) return null;

  return { newWins, newLosses, newTies, newBadges };
}

/**
 * Resolves pending forecasts, diffs the result against the last time this
 * was called, then marks everything as seen. Call once when Overview mounts.
 */
async function checkIn(): Promise<{
  state: ForecastGameState;
  recap: RecapDiff | null;
  justResolved: ResolvedForecast[];
}> {
  return enqueue(async () => {
    const { state, justResolved } = await resolvePendingUnlocked();
    const recap = computeRecap(state);

    const seenState = await saveState({
      ...state,
      lastSeen: {
        wins: state.stats.wins,
        losses: state.stats.losses,
        ties: state.stats.ties,
        badges: state.stats.badges,
      },
    });

    return { state: seenState, recap, justResolved };
  });
}

const forecastGameService = {
  getState,
  recordForecast,
  removeForecast,
  resolvePending,
  checkIn,
  togglePinnedBadge,
};

export default forecastGameService;
